import React, { useEffect, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { api, inr } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Star, Truck, RefreshCw, ShieldCheck, MessageCircle, Check, Instagram } from "lucide-react";
import ProductCard from "@/components/ProductCard";

export default function ProductDetail() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const { add } = useCart();
  const [p, setP] = useState(null);
  const [related, setRelated] = useState([]);
  const [activeImg, setActiveImg] = useState(0);
  const [size, setSize] = useState("");
  const [color, setColor] = useState("");
  const [pincode, setPincode] = useState("");
  const [pinResult, setPinResult] = useState(null);
  const [ownerPhone, setOwnerPhone] = useState("");
  const [instagram, setInstagram] = useState("");
  const [reviews, setReviews] = useState({ reviews: [], average: 0, count: 0 });

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get(`/products/${id}`).then((r) => {
      const wanted = params.get("color");
      const colors = r.data.colors || [];
      const picked = wanted && colors.includes(wanted) ? wanted : (colors[0] || "");
      setP(r.data); setSize(r.data.sizes?.[0] || ""); setColor(picked);
      api.get("/products", { params: { category: r.data.category } }).then((rr) => setRelated(rr.data.filter((x) => x.id !== id).slice(0, 4)));
    }).catch(() => {});
    api.get("/config").then((r) => { setOwnerPhone(r.data.whatsapp_owner || ""); setInstagram(r.data.instagram || ""); }).catch(() => {});
    api.get(`/products/${id}/reviews`).then((r) => setReviews(r.data)).catch(() => {});
  }, [id]);

  if (!p) return <div className="h-[60vh] flex items-center justify-center text-muted-foreground">Loading...</div>;
  const discount = p.mrp > p.price ? Math.round(((p.mrp - p.price) / p.mrp) * 100) : 0;
  const variant = (p.variants || []).find((v) => v.color === color);
  const gallery = (variant?.images?.length ? variant.images : p.images) || [];

  const checkPin = () => {
    if (pincode.length !== 6) { toast.error("Enter a valid 6-digit pincode"); return; }
    const days = 2 + (parseInt(pincode[0]) % 4);
    setPinResult({ serviceable: true, days });
  };

  const handleAdd = () => {
    if (!size) { toast.error("Please select a size"); return; }
    add(p, size, color, 1, variant?.sku || p.sku);
  };

  const buyWhatsApp = () => {
    const text = `Hi Million Pixels, I want to order:\n${p.name} (${p.sku})\nSize: ${size}, Color: ${color}\nPrice: ${inr(p.price)}`;
    const url = ownerPhone ? `https://wa.me/${ownerPhone.replace("+", "")}?text=${encodeURIComponent(text)}` : `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(url, "_blank");
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-8">
      <div className="text-xs text-muted-foreground mb-6 font-accent"><Link to="/shop">Shop</Link> / <Link to={`/shop?category=${encodeURIComponent(p.category)}`}>{p.category}</Link> / <span className="text-rose-950">{p.name}</span></div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-14">
        {/* Images */}
        <div className="flex flex-col-reverse lg:flex-row gap-4">
          <div className="flex lg:flex-col gap-3">
            {gallery.map((im, i) => (
              <button key={i} onClick={() => setActiveImg(i)} className={`w-16 h-20 rounded-lg overflow-hidden border-2 ${activeImg === i ? "border-rose-950" : "border-transparent"}`} data-testid={`pdp-thumb-${i}`}>
                <img src={im} alt="" className="img-cover" />
              </button>
            ))}
          </div>
          <div className="flex-1 rounded-2xl overflow-hidden bg-white aspect-[3/4] rw-zoom">
            <img src={gallery[activeImg]} alt={p.name} className="img-cover" data-testid="pdp-main-image" />
          </div>
        </div>

        {/* Info */}
        <div>
          <p className="accent-label">{p.category} · {p.fabric}</p>
          <h1 className="font-serif text-3xl lg:text-4xl font-bold text-rose-950 mt-2 leading-tight">{p.name}</h1>
          <div className="flex items-center gap-3 mt-3">
            <span className="flex items-center gap-1 text-sm text-amber-600"><Star className="w-4 h-4 fill-amber-500 text-amber-500" />{p.rating}</span>
            <span className="text-xs text-muted-foreground">SKU: {variant?.sku || p.sku}</span>
          </div>
          <div className="flex items-center gap-3 mt-5">
            <span className="font-serif text-3xl font-bold text-rose-950">{inr(p.price)}</span>
            {discount > 0 && <><span className="text-lg text-muted-foreground line-through">{inr(p.mrp)}</span><span className="bg-emerald-100 text-emerald-700 text-xs font-bold px-2 py-1 rounded">{discount}% OFF</span></>}
          </div>
          <p className="text-xs text-muted-foreground mt-1">Inclusive of all taxes</p>

          <div className="mt-6">
            <p className="text-sm font-semibold mb-2">Color: <span className="font-normal">{color}</span></p>
            <div className="flex gap-2">
              {p.colors?.map((c) => (
                <button key={c} onClick={() => { setColor(c); setActiveImg(0); }} className={`px-4 py-2 rounded-full text-sm border transition-colors ${color === c ? "border-rose-950 bg-rose-950 text-amber-50" : "border-rose-900/20"}`} data-testid={`pdp-color-${c}`}>{c}</button>
              ))}
            </div>
          </div>

          <div className="mt-5">
            <div className="flex justify-between items-center mb-2"><p className="text-sm font-semibold">Select Size</p><span className="text-xs text-amber-600 cursor-pointer">Size Guide</span></div>
            <div className="flex flex-wrap gap-2">
              {p.sizes?.map((s) => (
                <button key={s} onClick={() => setSize(s)} className={`w-12 h-12 rounded-lg text-sm font-medium border transition-colors ${size === s ? "border-rose-950 bg-rose-950 text-amber-50" : "border-rose-900/20 hover:border-rose-950"}`} data-testid={`pdp-size-${s}`}>{s}</button>
              ))}
            </div>
          </div>

          <div className="flex gap-3 mt-7">
            <Button onClick={handleAdd} className="flex-1 bg-rose-950 hover:bg-rose-900 h-13 h-12 text-base rounded-full" data-testid="pdp-add-to-cart">Add to Bag</Button>
            <Button onClick={buyWhatsApp} className="flex-1 bg-[#25D366] hover:bg-[#1da851] h-12 text-base rounded-full" data-testid="pdp-buy-whatsapp"><MessageCircle className="w-4 h-4 mr-2" />Buy via WhatsApp</Button>
          </div>

          {/* Pincode */}
          <div className="mt-7 border border-rose-900/15 rounded-xl p-4">
            <p className="text-sm font-semibold flex items-center gap-2 mb-3"><Truck className="w-4 h-4 text-rose-950" />Check Delivery (Shiprocket)</p>
            <div className="flex gap-2">
              <Input value={pincode} onChange={(e) => setPincode(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="Enter pincode" className="h-11" data-testid="pdp-pincode-input" />
              <Button variant="outline" onClick={checkPin} className="h-11 border-rose-950/30" data-testid="pdp-pincode-check">Check</Button>
            </div>
            {pinResult && <p className="text-sm text-emerald-700 mt-3 flex items-center gap-1" data-testid="pdp-pincode-result"><Check className="w-4 h-4" />Deliverable · Estimated in {pinResult.days}-{pinResult.days + 1} days</p>}
          </div>

          <div className="grid grid-cols-3 gap-3 mt-6 text-center">
            {[[RefreshCw, "3-Day Returns"], [ShieldCheck, "Secure Payment"], [MessageCircle, "WhatsApp Updates"]].map(([Icon, t], i) => (
              <div key={i} className="flex flex-col items-center gap-1.5 text-xs text-muted-foreground"><Icon className="w-5 h-5 text-amber-600" />{t}</div>
            ))}
          </div>

          <div className="mt-7 border-t border-rose-900/10 pt-5">
            <h3 className="font-serif text-lg font-semibold text-rose-950 mb-2">Product Details</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">{p.description}</p>
            {p.bullets?.length > 0 && (
              <ul className="mt-3 space-y-1.5" data-testid="pdp-bullets">
                {p.bullets.map((b, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-rose-950/80"><Check className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />{b}</li>
                ))}
              </ul>
            )}
          </div>

          {p.video && (
            <div className="mt-7 border-t border-rose-900/10 pt-5">
              <h3 className="font-serif text-lg font-semibold text-rose-950 mb-3">Product Video</h3>
              <video src={p.video} controls playsInline className="w-full rounded-xl bg-black max-h-[480px]" data-testid="pdp-video" />
            </div>
          )}
        </div>
      </div>

      {/* REVIEWS */}
      <div className="mt-20">
        <div className="flex items-center gap-3 mb-6">
          <h2 className="font-serif text-2xl lg:text-3xl font-bold text-rose-950">Customer Reviews</h2>
          {reviews.count > 0 && <span className="flex items-center gap-1 text-amber-600"><Star className="w-5 h-5 fill-amber-500 text-amber-500" />{reviews.average} ({reviews.count})</span>}
        </div>
        {reviews.count === 0 ? (
          <p className="text-muted-foreground text-sm" data-testid="pdp-no-reviews">No reviews yet. Reviews with photos can be added by customers after their order is delivered (from My Account).</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="pdp-reviews">
            {reviews.reviews.map((rv) => (
              <div key={rv.id} className="bg-white border border-rose-900/10 rounded-xl p-4 flex gap-4">
                {rv.photo_url && <img src={rv.photo_url} alt="review" className="w-20 h-24 object-cover rounded-lg" />}
                <div>
                  <div className="flex gap-0.5 text-amber-500 mb-1">{[...Array(5)].map((_, j) => <Star key={j} className={`w-3.5 h-3.5 ${j < rv.rating ? "fill-amber-500" : "text-rose-900/20"}`} />)}</div>
                  <p className="text-sm text-muted-foreground leading-relaxed">{rv.text}</p>
                  <p className="font-serif font-semibold text-rose-950 text-sm mt-2">{rv.customer_name}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* INSTAGRAM FOLLOW */}
      {instagram && (
        <a href={instagram} target="_blank" rel="noreferrer" className="mt-16 flex items-center justify-center gap-3 bg-gradient-to-r from-rose-950 to-amber-700 text-amber-50 rounded-2xl py-6 hover:opacity-95 transition-opacity" data-testid="pdp-instagram-follow">
          <Instagram className="w-6 h-6" />
          <span className="font-serif text-lg font-semibold">Follow us on Instagram for new drops & offers</span>
        </a>
      )}

      {related.length > 0 && (
        <div className="mt-20">
          <h2 className="font-serif text-2xl lg:text-3xl font-bold text-rose-950 mb-8">You May Also Love</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">{related.map((r, i) => <ProductCard key={r.id} p={r} index={i} />)}</div>
        </div>
      )}
    </div>
  );
}
